import type { FC, ReactNode } from "react"
import React from "react"
import * as Sentry from "@sentry/react"
import { useErrorBoundary } from "use-error-boundary"
import { InfoBox } from "@bancacfplus/InfoBox"
import { ReactComponent as ErrorIcon } from "@bancacfplus/Icons/assets/error.svg"
import type { AxiosError } from "axios"
import type { ApolloError } from "@apollo/client"
import style from "./ErrorBoundary.module.scss"
import { useTokenData } from "../../hooks/useTokenData"
import { Loading } from "@bancacfplus/Loading"

type ErrorBoundaryProps = {
  error?: Error | AxiosError | ApolloError
  swallow?: boolean
  message?: ReactNode
  title?: ReactNode
  errorContainer?: boolean
  infobox?: boolean
  reload?: boolean
  className?: string
  children?: ReactNode
}

const showReportDialog = () => {
  return Sentry.showReportDialog({
    lang: "it",
    title: "Scrivici cosa è successo!",
    subtitle: "Il nostro team è già stato informato.",
    subtitle2: "Per velocizzare la risoluzione, compila il form.",
    labelSubmit: "Invia",
    successMessage: "Grazie! Ti contatteremo appena il problema sarà risolto!",
    errorFormEntry: "Qualche campo non è valido. Per favore, correggi gli errori e prova ancora."
  })
}

export const ErrorBoundary: FC<ErrorBoundaryProps> = ({
  error,
  swallow = false,
  title = "Ops… qualcosa è andato storto.",
  message = "Si è verificato un problema, prova a ricaricare la pagina",
  reload = true,
  infobox = false,
  children,
  className
}) => {
  const { data, isLoading } = useTokenData()

  const logErrorOnSentry = e => {
    console.error("Error: ", e)
    Sentry.setUser({ email: data?.email ?? "email not found" })
    Sentry.captureException(error)
  }

  const { ErrorBoundary, didCatch } = useErrorBoundary({
    onDidCatch: logErrorOnSentry
  })

  if (isLoading) return <Loading />

  if (didCatch || error) {
    if (error) {
      logErrorOnSentry(error)
    }

    if (swallow) {
      return <></>
    }

    if (infobox)
      <div className={`${style.errorContainer} ${className ?? ""}`}>
        <InfoBox type="error">
          {message}
          <InfoBox.Action onClick={logErrorOnSentry}>Segnalaci l’errore</InfoBox.Action>
        </InfoBox>
      </div>

    return (
      <div className={`${style.wrap} ${className ?? ""}`}>
        <div className={style.icon}>
          <ErrorIcon />
        </div>
        <div className={style.content}>
          <h3>{title}</h3>
          <p>{message}</p>
          {reload && (
            <button
              className={style.button}
              onClick={() => {
                window.location.reload()
              }}
            >
              Ricarica la pagina
            </button>
          )}
          <p>o</p>
          <a onClick={showReportDialog}>Segnalaci l&apos;errore</a>
        </div>
      </div>
    )
  }

  return <ErrorBoundary>{children}</ErrorBoundary>
}
